/**
 * Regras puras da "Qualificação exigida" (Variação B, G1): projeção atalhos ↔ árvore,
 * validação e resumo em linguagem natural. A árvore é a do backend (SPEC §3.2, Adendo A7).
 */
import type { RuleBuilderConfig } from '@/components/designSystem/RuleBuilder/types'
import { parseBrDate } from '@/modules/company/Approvals/ProfessionalDocuments/model'
import { ProfessionalSealKey } from '@/types/professional-documents.types'
import {
  QualificationOnFail,
  QualificationOperator,
  QualificationRuleField
} from '@/types/qualification-eligibility.types'
import type {
  QualificationRuleChild,
  QualificationRuleGroup,
  QualificationRuleValue,
  SchedulePolicyLink
} from '@/types/qualification-policy.types'
import { formatIsoDateOnlyBR } from '@/utils/date'

/** Atalho Suporte a Vida = OU/E de ATLS, ACLS, PALS (D35), na ordem do catálogo. */
export const LIFE_SUPPORT_KEYS = ['ATLS', 'ACLS', 'PALS']

/** Limites de largura do validador do backend (`qualification-rules.validator.ts`, A7). */
export const MAX_RULE_CHILDREN = 20
export const MAX_RULE_NODES = 100

export const QUALIFICATION_RULE_BUILDER_CONFIG: RuleBuilderConfig<QualificationOperator> = {
  operators: [
    {
      value: QualificationOperator.AND,
      label: 'Todos - E',
      description: 'O médico precisa atender a todos os critérios'
    },
    {
      value: QualificationOperator.OR,
      label: 'Qualquer um - OU',
      description: 'Basta atender a um dos critérios'
    }
  ],
  defaultOperator: QualificationOperator.AND,
  addRuleLabel: 'Critério',
  addGroupLabel: 'Grupo',
  // Grupos nos níveis 0, 1 e 2 = profundidade ≤ 3 contando a raiz (`MAX_RULE_DEPTH` do backend).
  maxDepth: 2,
  allowDragDrop: true,
  minChildrenForOperator: 2,
  ruleLabel: (n) => `Critério ${n}`
}

export const ON_FAIL_OPTIONS: { value: QualificationOnFail; label: string }[] = [
  { value: QualificationOnFail.BLOCK, label: 'Bloquear' },
  { value: QualificationOnFail.PRE_ALLOCATE, label: 'Permitir pré-alocação' }
]

const ON_FAIL_TEXT: Record<QualificationOnFail, string> = {
  [QualificationOnFail.BLOCK]: 'bloqueia',
  [QualificationOnFail.PRE_ALLOCATE]: 'permite pré-alocação'
}

const OPERATOR_TEXT: Record<QualificationOperator, string> = {
  [QualificationOperator.AND]: 'Todos - E',
  [QualificationOperator.OR]: 'Qualquer um - OU'
}

export const emptyPolicy = (): QualificationRuleGroup => ({
  type: 'group',
  operator: QualificationOperator.AND,
  children: []
})
export const clonePolicy = (policy: QualificationRuleGroup): QualificationRuleGroup =>
  JSON.parse(JSON.stringify(policy)) as QualificationRuleGroup
export const isPolicyEmpty = (policy: QualificationRuleGroup): boolean =>
  policy.children.length === 0

const rule = (
  field: QualificationRuleField,
  key: string,
  onFail: QualificationOnFail
): QualificationRuleChild => ({ type: 'rule', value: { field, key, onFail } })

/** Valores das folhas diretas do grupo (`null` para subgrupo ou critério em edição). */
const leafValues = (group: QualificationRuleGroup): (QualificationRuleValue | null)[] =>
  group.children.map((child) => (child.type === 'rule' ? child.value : null))

const isDocument = (value: QualificationRuleValue | null): value is QualificationRuleValue =>
  !!value && value.field === QualificationRuleField.DOCUMENT && !!value.key

// --- atalhos -----------------------------------------------------------------

export type LifeSupportMode = 'ANY' | 'ALL'

export interface RequirementShortcuts {
  minimumDocs: boolean
  minimumDocsOnFail: QualificationOnFail
  lifeSupport: boolean
  lifeSupportMode: LifeSupportMode
  lifeSupportKeys: string[]
  lifeSupportOnFail: QualificationOnFail
  extraDocuments: { key: string; onFail: QualificationOnFail }[]
}

export const emptyShortcuts = (): RequirementShortcuts => ({
  minimumDocs: false,
  minimumDocsOnFail: QualificationOnFail.BLOCK,
  lifeSupport: false,
  lifeSupportMode: 'ANY',
  lifeSupportKeys: [...LIFE_SUPPORT_KEYS],
  lifeSupportOnFail: QualificationOnFail.BLOCK,
  extraDocuments: []
})

/** Itens marcados são exigidos juntos (raiz E). Suporte a Vida vira sempre um grupo (mesmo com 1). */
export function shortcutsToPolicy(shortcuts: RequirementShortcuts): QualificationRuleGroup {
  const policy = emptyPolicy()
  if (shortcuts.minimumDocs) {
    policy.children.push(
      rule(
        QualificationRuleField.SEAL,
        ProfessionalSealKey.MINIMUM_DOCS,
        shortcuts.minimumDocsOnFail
      )
    )
  }
  if (shortcuts.lifeSupport && shortcuts.lifeSupportKeys.length) {
    policy.children.push({
      type: 'group',
      operator:
        shortcuts.lifeSupportMode === 'ALL' ? QualificationOperator.AND : QualificationOperator.OR,
      children: shortcuts.lifeSupportKeys.map((key) =>
        rule(QualificationRuleField.DOCUMENT, key, shortcuts.lifeSupportOnFail)
      )
    })
  }
  shortcuts.extraDocuments.forEach((doc) =>
    policy.children.push(rule(QualificationRuleField.DOCUMENT, doc.key, doc.onFail))
  )
  return policy
}

type LifeSupportShortcut = Pick<
  RequirementShortcuts,
  'lifeSupport' | 'lifeSupportMode' | 'lifeSupportKeys' | 'lifeSupportOnFail'
>

/** Grupo só de ATLS/ACLS/PALS, sem repetição e com a mesma ação; senão `null`. */
function asLifeSupport(group: QualificationRuleGroup): LifeSupportShortcut | null {
  const values = leafValues(group)
  if (!values.length || !values.every((v) => isDocument(v) && LIFE_SUPPORT_KEYS.includes(v.key))) {
    return null
  }
  const rules = values as QualificationRuleValue[]
  const keys = LIFE_SUPPORT_KEYS.filter((key) => rules.some((v) => v.key === key))
  if (keys.length !== rules.length || new Set(rules.map((v) => v.onFail)).size !== 1) return null
  return {
    lifeSupport: true,
    lifeSupportMode: group.operator === QualificationOperator.AND ? 'ALL' : 'ANY',
    lifeSupportKeys: keys,
    lifeSupportOnFail: rules[0].onFail
  }
}

/** Aplica um filho da raiz aos atalhos; `false` quando ele não cabe nos atalhos. */
function applyToShortcuts(shortcuts: RequirementShortcuts, child: QualificationRuleChild): boolean {
  if (child.type === 'group') {
    const lifeSupport = asLifeSupport(child)
    if (!lifeSupport || shortcuts.lifeSupport) return false
    Object.assign(shortcuts, lifeSupport)
    return true
  }
  const value = child.value
  if (!value?.key) return false
  const isMinimumDocs =
    value.field === QualificationRuleField.SEAL && value.key === ProfessionalSealKey.MINIMUM_DOCS
  if (isMinimumDocs && !shortcuts.minimumDocs) {
    shortcuts.minimumDocs = true
    shortcuts.minimumDocsOnFail = value.onFail
    return true
  }
  if (!isDocument(value) || shortcuts.extraDocuments.some((d) => d.key === value.key)) return false
  shortcuts.extraDocuments.push({ key: value.key, onFail: value.onFail })
  return true
}

/** Árvore → atalhos; `null` quando há algo que os atalhos não mostram (abre o modo avançado). */
export function policyToShortcuts(policy: QualificationRuleGroup): RequirementShortcuts | null {
  if (policy.operator !== QualificationOperator.AND && policy.children.length > 1) return null
  const shortcuts: RequirementShortcuts = { ...emptyShortcuts(), extraDocuments: [] }
  return policy.children.every((child) => applyToShortcuts(shortcuts, child)) ? shortcuts : null
}

export function shortcutErrors(shortcuts: RequirementShortcuts): string[] {
  return shortcuts.lifeSupport && !shortcuts.lifeSupportKeys.length
    ? ['Suporte a Vida: escolha ao menos um certificado (ATLS, ACLS ou PALS) ou desmarque o item.']
    : []
}

// --- validação -----------------------------------------------------------------

/** Erros com caminho legível; raiz vazia é válida (= nenhuma exigência). Espelha o backend. */
export function validatePolicy(policy: QualificationRuleGroup): string[] {
  const errors: string[] = []
  let nodes = 1
  const visit = (group: QualificationRuleGroup, path: string) => {
    let ruleCount = 0
    let groupCount = 0
    group.children.forEach((child) => {
      nodes += 1
      if (child.type === 'rule') {
        ruleCount += 1
        const label = `${path}Critério ${ruleCount}`
        if (!child.value?.key) errors.push(`${label}: escolha o campo e o valor.`)
        else if (
          child.value.field === QualificationRuleField.SEAL &&
          child.value.key === ProfessionalSealKey.LIFE_SUPPORT
        ) {
          errors.push(
            `${label}: Suporte a Vida é um atalho de documentos (ATLS/ACLS/PALS), não um selo.`
          )
        }
        return
      }
      groupCount += 1
      const label = `${path}Grupo ${groupCount} (${OPERATOR_TEXT[child.operator]})`
      if (!child.children.length) {
        errors.push(`${label}: grupo vazio — adicione ao menos um critério ou remova o grupo.`)
        return
      }
      if (child.children.length > MAX_RULE_CHILDREN) {
        errors.push(`${label}: mais de ${MAX_RULE_CHILDREN} itens — agrupe ou remova critérios.`)
      }
      visit(child, `${label} › `)
    })
  }
  if (policy.children.length > MAX_RULE_CHILDREN) {
    errors.push(`A raiz tem mais de ${MAX_RULE_CHILDREN} itens: agrupe ou remova critérios.`)
  }
  visit(policy, '')
  if (nodes > MAX_RULE_NODES) {
    errors.push(
      `A qualificação tem mais de ${MAX_RULE_NODES} critérios e grupos: simplifique as regras.`
    )
  }
  return errors
}

// --- resumo --------------------------------------------------------------------

export type CriterionNameOf = (value: QualificationRuleValue) => string

function humanList(items: string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`
}

/** Grupo só de documentos com a mesma ação vira uma frase só ("pelo menos um de ATLS/ACLS"). */
function describeDocumentGroup(
  group: QualificationRuleGroup,
  nameOf: CriterionNameOf
): string | null {
  const values = leafValues(group)
  if (values.length < 2 || !values.every(isDocument)) return null
  const rules = values as QualificationRuleValue[]
  if (new Set(rules.map((v) => v.onFail)).size !== 1) return null
  const names = rules.map(nameOf)
  const action = ON_FAIL_TEXT[rules[0].onFail]
  return group.operator === QualificationOperator.OR
    ? `pelo menos um de ${names.join('/')} (${action})`
    : `${humanList(names)} (${action})`
}

function describe(child: QualificationRuleChild, nested: boolean, nameOf: CriterionNameOf): string {
  if (child.type === 'rule') {
    const value = child.value
    if (!value?.key) return '[critério incompleto]'
    return `${nameOf(value)} (${ON_FAIL_TEXT[value.onFail]})`
  }
  if (!child.children.length) return '[grupo vazio]'
  const documentGroup = describeDocumentGroup(child, nameOf)
  if (documentGroup) return documentGroup
  const isOr = child.operator === QualificationOperator.OR
  const body = child.children
    .map((item) => describe(item, true, nameOf))
    .join(isOr ? ' OU ' : ' E ')
  if (!nested) return body
  return isOr ? `pelo menos um de: (${body})` : `todos de: (${body})`
}

export function summarizePolicy(policy: QualificationRuleGroup, nameOf: CriterionNameOf): string {
  if (!policy.children.length) {
    return 'Nenhuma qualificação exigida: qualquer médico vinculado pode ser alocado nesta escala.'
  }
  return `Exige ${describe(policy, false, nameOf)}.`
}

export function countByOnFail(policy: QualificationRuleGroup): Record<QualificationOnFail, number> {
  const count: Record<QualificationOnFail, number> = {
    [QualificationOnFail.BLOCK]: 0,
    [QualificationOnFail.PRE_ALLOCATE]: 0
  }
  const visit = (child: QualificationRuleChild) => {
    if (child.type === 'group') return child.children.forEach(visit)
    if (child.value?.key) count[child.value.onFail] += 1
  }
  policy.children.forEach(visit)
  return count
}

// --- edição de critério -------------------------------------------------------

export const setCriterionField = (
  value: QualificationRuleValue | null,
  field: QualificationRuleField
): QualificationRuleValue => ({
  field,
  key: '',
  onFail: value?.onFail ?? QualificationOnFail.BLOCK
})

export const setCriterionKey = (
  value: QualificationRuleValue | null,
  key: string
): QualificationRuleValue => ({
  field: value?.field ?? QualificationRuleField.DOCUMENT,
  key,
  onFail: value?.onFail ?? QualificationOnFail.BLOCK
})

export const setCriterionOnFail = (
  value: QualificationRuleValue | null,
  onFail: QualificationOnFail
): QualificationRuleValue => ({
  field: value?.field ?? QualificationRuleField.DOCUMENT,
  key: value?.key ?? '',
  onFail
})

// --- vigência (D61) -------------------------------------------------------------

const nextIsoDay = (iso: string): string => {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day + 1)).toISOString().slice(0, 10)
}

/**
 * Primeira vigência que o backend aceita (`assertNoOverlap`, vigência inclusiva): o vínculo
 * aberto é editado no lugar a partir do próprio início; um fechado libera o dia seguinte ao fim.
 */
export function earliestValidFrom(history: SchedulePolicyLink[], today: string): string {
  return history.reduce((earliest, link) => {
    const free = link.validTo === null ? link.validFrom : nextIsoDay(link.validTo)
    return free > earliest ? free : earliest
  }, today)
}

/** "dd/mm/aaaa" válido, ≥ hoje (America/Sao_Paulo, "aaaa-mm-dd") e ≥ `earliest` (agendado). */
export function validFromError(value: string, today: string, earliest = today): string | null {
  const iso = parseBrDate(value)
  if (!iso) return 'Informe a data no formato dd/mm/aaaa'
  if (iso < today) return 'A vigência não pode começar no passado'
  if (iso < earliest)
    return `Já há requisitos programados nesta escala: escolha uma data a partir de ${formatIsoDateOnlyBR(earliest)}`
  return null
}
